// The panel behind a bulk-grid column heading: sort, a typed Condition tab, and a Values checkbox
// list. Draft state lives here and nothing reaches the server until Apply — with 566 projects behind
// every keystroke, an explicit commit beats a debounce.
//
// Two rules this file exists to enforce:
//  - Operands are coerced with projectManagementToStored, the SAME function the cell editor commits
//    with, so a filter boundary and a typed cell can never disagree about shape. DateInput hands back
//    a 'YYYY-MM-DD' string, not a Date.
//  - Date operators are resolved HERE into an absolute half-open interval [date, date2) in the user's
//    timezone. The server never parses a date: prod runs UTC and would shift every boundary 7-8 hours.

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { Checkbox } from '/imports/ui/Checkbox';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { projectManagementValueInput, projectManagementToStored } from '/imports/ui/ProjectManagementCellEditor';
import {
  ProjectManagementBulkColumn, ProjectManagementBulkColumnFilter, ProjectManagementBulkColumnValue,
  ProjectManagementBulkFilterOp, ProjectManagementParameterType,
  projectManagementBulkFilterIsInert, projectManagementFormatValue,
} from '/imports/api/projectManagement';

export const ColumnFilterStyled = styled.div`
  padding: 0.5rem;
  .sort-row { display: flex; gap: 0.3rem; margin-bottom: 0.5rem; }
  .sort-row .btn { flex: 1; height: 1.8rem; padding: 0 0.4rem; font-size: 0.75rem; }
  .sort-row .btn.on { background: ${css.primaryColor}; color: ${css.white}; }
  .tabs { display: flex; gap: 0.3rem; border-bottom: 1px solid ${css.grey7}; margin-bottom: 0.5rem; }
  .tabs button {
    background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer;
    padding: 0.25rem 0.4rem; font-size: 0.78rem; color: ${css.grey3};
  }
  .tabs button.on { color: ${css.primaryColor}; border-bottom-color: ${css.primaryColor}; }
  .operand { margin-top: 0.4rem; display: flex; flex-direction: column; gap: 0.3rem; }
  .operand input, .operand select { width: 100%; box-sizing: border-box; height: 1.9rem; font-size: 0.8rem; }
  .values { max-height: 15rem; overflow: auto; margin-top: 0.4rem; }
  .values .opt { display: flex; align-items: center; gap: 0.35rem; padding: 0.1rem 0; font-size: 0.8rem; }
  .values .opt .count { margin-left: auto; color: ${css.grey5}; font-size: 0.72rem; }
  .values .opt .label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .values-tools { display: flex; gap: 0.5rem; margin-top: 0.3rem; }
  .values-tools button { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.75rem; }
  .hint { color: ${css.grey4}; font-size: 0.72rem; margin-top: 0.3rem; }
  .buttons { display: flex; gap: 0.4rem; margin-top: 0.6rem; }
  .buttons .btn { flex: 1; height: 1.9rem; padding: 0 0.5rem; font-size: 0.78rem; }
`;

type FilterType = ProjectManagementParameterType | 'document';

/** Operators offered per column type, in menu order. */
const opsFor = (type: FilterType): ProjectManagementBulkFilterOp[] => {
  switch (type) {
    case 'document': return ['hasFile', 'noFile'];
    case 'boolean': return ['isTrue', 'isFalse', 'isBlank'];
    case 'date': case 'datetime':
      return ['is', 'before', 'onOrBefore', 'after', 'onOrAfter', 'between', 'isBlank', 'isNotBlank'];
    case 'string':
      return ['contains', 'notContains', 'is', 'isNot', 'startsWith', 'endsWith', 'isBlank', 'isNotBlank'];
    default:
      return ['is', 'isNot', 'gt', 'gte', 'lt', 'lte', 'between', 'isBlank', 'isNotBlank'];
  }
};

const opLabel = (op: ProjectManagementBulkFilterOp): string => ({
  contains: strings.Contains(), notContains: strings.DoesNotContain(), is: strings.IsExactly(),
  isNot: strings.IsNot(), startsWith: strings.StartsWith(), endsWith: strings.EndsWith(),
  gt: strings.GreaterThan(), gte: strings.GreaterOrEqual(), lt: strings.LessThan(), lte: strings.LessOrEqual(),
  between: strings.Between(), before: strings.Before(), onOrBefore: strings.OnOrBefore(),
  after: strings.After(), onOrAfter: strings.OnOrAfter(), isTrue: strings.IsTrue(), isFalse: strings.IsFalse(),
  isBlank: strings.IsBlank(), isNotBlank: strings.IsNotBlank(), hasFile: strings.HasFile(),
  noFile: strings.HasNoFile(), values: strings.Values(),
}[op] || op);

/** Which operand widgets an operator needs. */
const operandShape = (op: ProjectManagementBulkFilterOp, type: FilterType): 'none' | 'text' | 'num' | 'num2' | 'date' | 'date2' => {
  if (['isBlank', 'isNotBlank', 'isTrue', 'isFalse', 'hasFile', 'noFile'].includes(op)) return 'none';
  const dated = type === 'date' || type === 'datetime';
  if (op === 'between') return dated ? 'date2' : 'num2';
  if (['before', 'onOrBefore', 'after', 'onOrAfter'].includes(op)) return 'date';
  if (op === 'is' || op === 'isNot') return dated ? 'date' : (type === 'string' ? 'text' : 'num');
  if (['gt', 'gte', 'lt', 'lte'].includes(op)) return 'num';
  return 'text';
};

/** Local midnight of the picked day — the same coercion the cell editor uses. */
const dayStart = (raw: any): Date | undefined => {
  const d = projectManagementToStored('date', raw);
  return d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
};
/** The next local midnight. Calendar arithmetic, so it stays right across a DST change. */
const nextDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

/** Turns the draft operands into the wire filter, resolving dates to a half-open [date, date2). */
const buildFilter = (
  column: ProjectManagementBulkColumn, op: ProjectManagementBulkFilterOp, type: FilterType,
  raw: { text?: string, a?: any, b?: any },
): ProjectManagementBulkColumnFilter => {
  const f: ProjectManagementBulkColumnFilter = { column, op };
  const shape = operandShape(op, type);
  if (shape === 'text') f.text = raw.text;
  if (shape === 'num' || shape === 'num2') {
    f.num = projectManagementToStored('number', raw.a) as number | undefined;
    if (shape === 'num2') f.num2 = projectManagementToStored('number', raw.b) as number | undefined;
  }
  if (shape === 'date' || shape === 'date2') {
    const a = dayStart(raw.a);
    const b = dayStart(raw.b);
    if (op === 'is' || op === 'isNot') { f.date = a; f.date2 = a && nextDay(a); }
    else if (op === 'before') f.date2 = a;
    else if (op === 'onOrBefore') f.date2 = a && nextDay(a);
    else if (op === 'after') f.date = a && nextDay(a);
    else if (op === 'onOrAfter') f.date = a;
    // `between` reads inclusively at both ends, as the From/To labels promise.
    else if (op === 'between') { f.date = a; f.date2 = b && nextDay(b); }
  }
  return f;
};

export interface ColumnFilterProps {
  column: ProjectManagementBulkColumn;
  label: string;
  type: FilterType;
  unit?: string;
  filter?: ProjectManagementBulkColumnFilter;
  sortDir?: 1 | -1;
  /** Fetches the distinct values for this column over the set filtered by every OTHER column. */
  loadValues(): Promise<{ values: ProjectManagementBulkColumnValue[], blankCount: number, truncated: boolean }>;
  onApply(filter?: ProjectManagementBulkColumnFilter): void;
  onSort(dir?: 1 | -1): void;
  onClose(): void;
}

export const ProjectManagementColumnFilter = (props: ColumnFilterProps) => {
  const { column, type, unit, filter, sortDir, onApply, onSort, onClose } = props;
  const ops = useMemo(() => opsFor(type), [type]);
  // Values are only meaningful where a cell has a value; a document row always exists (plan §1.1c).
  const canListValues = type !== 'document';

  const [tab, setTab] = useState<'condition' | 'values'>(filter?.op === 'values' && canListValues ? 'values' : 'condition');
  const [op, setOp] = useState<ProjectManagementBulkFilterOp>(filter && filter.op !== 'values' ? filter.op : ops[0]);
  const [text, setText] = useState(filter?.text || '');
  const [a, setA] = useState<any>();
  const [b, setB] = useState<any>();
  const [picked, setPicked] = useState<Set<string>>(new Set(filter?.op === 'values' ? filter.values || [] : []));
  const [blank, setBlank] = useState(filter?.op === 'values' ? !!filter.blank : false);
  const [valueSearch, setValueSearch] = useState('');
  const [loaded, setLoaded] = useState<{ values: ProjectManagementBulkColumnValue[], blankCount: number, truncated: boolean }>();
  const [loadingValues, setLoadingValues] = useState(false);

  // Fetch only when the Values tab is actually opened — 25 visible columns must not cost 25 scans.
  useEffect(() => {
    if (tab !== 'values' || loaded || loadingValues) return;
    setLoadingValues(true);
    props.loadValues()
      .then(setLoaded)
      .catch(() => setLoaded({ values: [], blankCount: 0, truncated: false }))
      .finally(() => setLoadingValues(false));
  }, [tab]);

  const shape = operandShape(op, type);
  const dated = type === 'date' || type === 'datetime';
  const operandInput = (value: any, onChange: (v: any) => void) =>
    projectManagementValueInput(dated ? 'date' : type === 'string' ? 'string' : 'number', value, onChange);

  /** Display label for one distinct value, formatted in the USER's timezone (the server sends raw). */
  const labelFor = (v: ProjectManagementBulkColumnValue) =>
    projectManagementFormatValue(v.value as any, unit, type === 'document' ? 'string' : type);

  const shownValues = (loaded?.values || []).filter((v) => {
    const q = valueSearch.trim().toLowerCase();
    return !q || labelFor(v).toLowerCase().includes(q);
  });

  const apply = () => {
    if (tab === 'values') {
      const next: ProjectManagementBulkColumnFilter = { column, op: 'values', values: [...picked], blank };
      onApply(projectManagementBulkFilterIsInert(next) ? undefined : next);
    }
    else {
      const next = buildFilter(column, op, type, { text, a, b });
      onApply(projectManagementBulkFilterIsInert(next) ? undefined : next);
    }
    onClose();
  };
  const clear = () => { onApply(undefined); onClose(); };

  return (
    <ColumnFilterStyled>
      <div className="sort-row">
        <Button className={`secondary compact${sortDir === 1 ? ' on' : ''}`} onClick={() => { onSort(sortDir === 1 ? undefined : 1); onClose(); }}>
          {strings.SortAscending()}
        </Button>
        <Button className={`secondary compact${sortDir === -1 ? ' on' : ''}`} onClick={() => { onSort(sortDir === -1 ? undefined : -1); onClose(); }}>
          {strings.SortDescending()}
        </Button>
      </div>
      {
        canListValues &&
        <div className="tabs">
          <button className={tab === 'condition' ? 'on' : ''} onClick={() => setTab('condition')}>{strings.Condition()}</button>
          <button className={tab === 'values' ? 'on' : ''} onClick={() => setTab('values')}>{strings.Values()}</button>
        </div>
      }
      {
        tab === 'condition' ?
          <>
            <Select value={op} options={ops.map((o) => ({ value: o, label: opLabel(o) }))} onChange={(v) => setOp(v as ProjectManagementBulkFilterOp)} />
            <div className="operand">
              {shape === 'text' && <Input value={text} onChange={(v) => setText(v || '')} placeholder={strings.Value()} onEnter={apply} />}
              {(shape === 'num' || shape === 'date') && operandInput(a, setA)}
              {
                (shape === 'num2' || shape === 'date2') &&
                <>
                  {operandInput(a, setA)}
                  {operandInput(b, setB)}
                </>
              }
            </div>
          </>
          :
          <>
            <Input value={valueSearch} onChange={(v) => setValueSearch(v || '')} placeholder={strings.Search()} />
            {
              loadingValues ?
                <LoadingFlex />
                :
                <div className="values">
                  <label className="opt">
                    <Checkbox checked={blank} onChange={(c) => setBlank(!!c)} />
                    <span className="label">{strings.Blanks()}</span>
                    <span className="count">{loaded?.blankCount ?? 0}</span>
                  </label>
                  {
                    shownValues.map((v) => (
                      <label className="opt" key={v.key}>
                        <Checkbox
                          checked={picked.has(v.key)}
                          onChange={(c) => setPicked((p) => {
                            const next = new Set(p);
                            if (c) next.add(v.key);
                            else next.delete(v.key);
                            return next;
                          })}
                        />
                        <span className="label" title={labelFor(v)}>{labelFor(v)}</span>
                        <span className="count">{v.count}</span>
                      </label>
                    ))
                  }
                  {!shownValues.length && !loaded?.blankCount && <div className="hint">{strings.NoMatchingValues()}</div>}
                </div>
            }
            <div className="values-tools">
              <button onClick={() => { setPicked(new Set(shownValues.map((v) => v.key))); setBlank(true); }}>{strings.SelectAll()}</button>
              <button onClick={() => { setPicked(new Set()); setBlank(false); }}>{strings.ClearColumns()}</button>
            </div>
            {loaded?.truncated && <div className="hint">{strings.MoreValuesNotShown()}</div>}
          </>
      }
      <div className="buttons">
        <Button className="secondary compact" disabled={!filter && !sortDir} onClick={clear}>{strings.ClearFilter()}</Button>
        <Button className="primary compact" onClick={apply}>{strings.Apply()}</Button>
      </div>
    </ColumnFilterStyled>
  );
};
